import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { General } from 'icons/untitledui';
import html2pdf from 'html2pdf.js';

// styles
import * as Buttons from 'theme/components/Buttons.style';
import { Step } from 'types/step';
import * as Styled from './_styles/PrintConversation.style';

interface PrintConversationProps {
  steps: Step[];
  onGeneratePDF?: () => Promise<void>;
  isOpen: boolean;
  onClose: () => void;
}

function PrintConversation({
  steps,
  onGeneratePDF,
  isOpen,
  onClose,
}: PrintConversationProps) {
  const { t } = useTranslation('pageEcoach');
  const contentRef = useRef<HTMLDivElement>(null);
  const printContentRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const getMessageContent = (output: any) => {
    // If there's a message, return it
    if (output.message) {
      // Strip HTML tags and convert HTML entities
      const stripHtml = (html: string) => {
        const tmp = document.createElement('DIV');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
      };

      return stripHtml(output.message);
    }

    // If there's no message but there's a RAG summary, return it
    if (output.rag?.summary) {
      return output.rag.summary;
    }

    return '';
  };

  const renderConversationItems = (output: any) => {
    if (!output.conversation_items?.length) return null;

    return (
      <Styled.ConversationItemsList>
        {output.conversation_items.map((item: any, index: number) => (
          <Styled.ConversationItem key={index}>
            <Styled.ConversationItemTitle>
              {item.title}
            </Styled.ConversationItemTitle>
            <Styled.ConversationItemDescription>
              {item.short_description}
            </Styled.ConversationItemDescription>
          </Styled.ConversationItem>
        ))}
      </Styled.ConversationItemsList>
    );
  };

  const handlePrint = async () => {
    if (!printContentRef.current) return;
    setError(null);

    const element = printContentRef.current;
    const opt = {
      margin: 0.5,
      filename: 'conversation.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        logging: true,
        letterRendering: true,
        windowHeight: element.scrollHeight,
      },
      jsPDF: {
        unit: 'in',
        format: 'letter',
        orientation: 'portrait',
      },
    } as const;

    try {
      await html2pdf().set(opt).from(element).save();
      onClose();
      if (onGeneratePDF) {
        await onGeneratePDF();
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError(t('convoPrep.ConversationItemDetails.pdfError'));
    }
  };

  if (!isOpen) return null;

  return (
    <Styled.ModalOverlay>
      <Styled.ModalContent ref={contentRef}>
        <Styled.CloseButtonSection>
          <General.XClose onClick={onClose} style={{ cursor: 'pointer' }}>
            ×
          </General.XClose>
        </Styled.CloseButtonSection>

        <Styled.MessageContainer>
          {steps.map((step, index) => (
            <div key={`step-${index}`}>
              {step.user_inputs?.map((input, inputIndex) => (
                <div key={`user-${inputIndex}`}>
                  <Styled.Timestamp>
                    {formatDate(step.created_at)}
                  </Styled.Timestamp>
                  <Styled.UserMessage>
                    <strong>{t('PrintConversation.You')}:</strong>{' '}
                    {input.message}
                  </Styled.UserMessage>
                </div>
              ))}

              {step.outputs?.map((output, outputIndex) => (
                <div key={`nurau-${outputIndex}`}>
                  <Styled.Timestamp>
                    {formatDate(step.created_at)}
                  </Styled.Timestamp>
                  <Styled.NurauMessage>
                    <strong>Nurau:</strong> {getMessageContent(output)}
                    {renderConversationItems(output)}
                  </Styled.NurauMessage>
                </div>
              ))}
            </div>
          ))}
        </Styled.MessageContainer>
        <Styled.PrintButtonSection>
          {error && (
            <div style={{ color: 'red', padding: '10px', textAlign: 'center' }}>
              {error}
            </div>
          )}
          <Buttons.SecondaryDark onClick={handlePrint}>
            {t('PrintConversation.Print')}
          </Buttons.SecondaryDark>
        </Styled.PrintButtonSection>
      </Styled.ModalContent>

      {/* Hidden print-only version */}
      <div style={{ display: 'none' }}>
        <div ref={printContentRef} style={{ padding: '20px' }}>
          {steps.map((step, index) => (
            <div key={`print-step-${index}`}>
              {step.user_inputs?.map((input, inputIndex) => (
                <div key={`print-user-${inputIndex}`}>
                  <div
                    style={{
                      color: '#666',
                      fontSize: '12px',
                      marginBottom: '4px',
                    }}
                  >
                    {formatDate(step.created_at)}
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <strong>{t('PrintConversation.You')}:</strong>{' '}
                    {input.message}
                  </div>
                </div>
              ))}

              {step.outputs?.map((output, outputIndex) => (
                <div key={`print-nurau-${outputIndex}`}>
                  <div
                    style={{
                      color: '#666',
                      fontSize: '12px',
                      marginBottom: '4px',
                    }}
                  >
                    {formatDate(step.created_at)}
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <strong>Nurau:</strong> {getMessageContent(output)}
                    {renderConversationItems(output)}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </Styled.ModalOverlay>
  );
}

export default PrintConversation;
